import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';
import { confirm, showAlert } from 'actions/app';
import {
  hideReferenceReleaseComposer as hideReferenceReleaseComposerAction,
  showReferenceReleaseComposer
} from 'actions/joborder';
import { generateCandidatePresentation } from 'features/ai-generators';
import {
  CallVerbiages,
  CandidatePresentationTypeSelector,
  CandidateRoleplayScenarioSelector,
  PresentationMakerTemplate
} from 'features/ai-generators/components';
import { CALL_VERBIAGES_TYPES } from 'features/ai-generators/constants';
import { CreationTaskDrawer } from 'features/daily-plan';
import { ProfileRestrictionDrawer, useProfileRestrictions } from 'features/profile-restrictions';
import useFetchWithStatus from 'hooks/fetchWithStatus';
import { useProfile } from 'hooks/profileActions';
import { useSearchProjectInProfiles } from 'hooks/searchProject';
import { useVerifyUrgencyDialogManager } from 'hooks/useVerifyUrgencyDialogManager';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import API from 'services/API';
import {
  canUserEditEntity,
  Permissions,
  userHasPermission,
  userHasRoles
} from 'services/Authorization';
import { CandidateIncludes } from 'services/includes';
import strings from 'strings';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import { When } from 'UI/components/atoms/When';
import { VerifyUrgencyDialog } from 'UI/components/molecules/VerifyUrgencyDialog';
import CandidateEdit from 'UI/components/organisms/CandidateEdit';
import AddHiringAuthority from 'UI/components/organisms/candidates/HiringAuthority/AddHiringAuthority';
import LinkHiringAuthorityDrawer from 'UI/components/organisms/candidates/HiringAuthority/LinkHiringAuthorityDrawer';
import ReferenceRelease from 'UI/components/organisms/candidates/ReferenceRelease';
import {
  ClaimAccountabilityDrawer,
  useClaimAccountability
} from 'UI/components/organisms/inventoryProfiles/ClaimAccountability';
import { PROFILE_DRAWER_TYPES } from 'UI/components/organisms/inventoryProfiles/constants';
import { getSendoutActions } from 'UI/components/organisms/inventoryProfiles/SendoutsTab/sendoutsActions';
import ToggleProfileModeButton from 'UI/components/organisms/inventoryProfiles/ToggleProfileModeButton';
import {
  getOptOutDate,
  handleEntityEditionCompleted
} from 'UI/components/organisms/inventoryProfiles/utils';
import LinkCompanyDialog from 'UI/components/organisms/LinkCompanyDialog';
import ReassignRecruiterForm from 'UI/components/organisms/ReassignRecruiter';
import SendoutManager from 'UI/components/organisms/sendouts';
import InventoryProfileLayout from 'UI/components/templates/InventoryProfileLayout';
import { DateFormats } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import {
  CandidateEntity,
  ENTITY_TYPE_ID_MAP,
  EntityType,
  entityTypes,
  SearchProjectEntities,
  TabKeys
} from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';
import { Roles } from 'UI/constants/roles';
import { RequestStatus, UIStatus } from 'UI/constants/status';
import useFetchCandidateDataSheet from 'UI/pages/EditCandidateDataSheet/hooks/useFetchCandidateDataSheet';
import { SvgCheck, SvgMakeAi } from 'UI/res';
import { getErrorMessage, localTimeFormatter, toLocalTime } from 'UI/utils';
import { completeReferenceRelease } from 'UI/utils/inventory';

import { useCandidateProfileFeatureFlags } from './CandidateProfile.hooks';
import { buildPresentationMakerCandidateParam, createTabsProps } from './CandidateProfile.utils';

/**
 * @param {Object} props
 * @param {boolean} props.enableUrlNavigation
 * @param {number} props.id
 * @param {Object} props.layoutClassName
 * @param {*} props.match
 * @param {Object} props.profileModeProps
 * @param {Function} props.onEditionCompleted
 * @param {Function} props.onVerificationCompleted
 */
const FPCandidateProfile = ({
  enableUrlNavigation,
  id,
  layoutClassName,
  match,
  onEditionCompleted,
  onVerificationCompleted,
  profileModeProps
}) => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isPresentationMakerEnabled = userHasPermission(Permissions.Inventory.PresentationMaker);
  const isRoleplayEnabled =
    checkIfFeatureFlagEnabled(FeatureFlags.AIRoleplay) &&
    userHasPermission(Permissions.AIIntegrations.Roleplay);

  const candidateId = match?.params?.id || id;
  const [currentUser] = useUserContext();
  const endpoint = `${Endpoints.Candidates}/${candidateId}`;
  const entityType = entityTypes.find(each => each.id === EntityType.Candidate);
  const history = useHistory();
  const dispatch = useDispatch();
  const { referenceRelease } = useSelector(state => state.jobOrder);
  const [selectedDrawerType, setSelectedDrawerType] = useState(PROFILE_DRAWER_TYPES.null);

  const handleSelectDrawerType = type => setSelectedDrawerType(type);

  const closeDrawer = () => setSelectedDrawerType(PROFILE_DRAWER_TYPES.null);
  const canUseDailyPlanTask = userHasPermission(Permissions.DailyPlan.CanUseModule);

  const {
    canRequestDoNotContact,
    canRequestDoNotEngage,
    canUseUrgencyVerification,
    isAddDailyPlanTaskEnabled,
    shouldShowKeywordTags
  } = useCandidateProfileFeatureFlags();

  const {
    isOpen: isOpenVerifyUrgencyDialog,
    closeDialog: closeVerifyUrgencyDialog,
    openDialog: openVerifyUrgencyDialog
  } = useVerifyUrgencyDialogManager();

  /** To keep track of tabs that should refresh after an event.
   * Remember that a component unmount when key changes */
  const [tabKeys, setTabKeys] = useState({
    [TabKeys.Sendouts]: 0,
    [TabKeys.Placements]: 1,
    [TabKeys.SearchProjects]: 2
  });

  const {
    handleEditClick,
    handleEditClosed,
    handleEditCompleted,
    handleProfileStateUpdate,
    handleReassignClick,
    handleReassignClosed,
    handleReassignCompleted,
    isLoading,
    refreshData,
    results,
    setUiState,
    state,
    status,
    uiState
  } = useProfile({
    entity: EntityType.Candidate,
    profileId: candidateId,
    includes: `${CandidateIncludes}${shouldShowKeywordTags ? ',keywordTags' : ''}`,
    shouldLoadTitle: enableUrlNavigation
  });

  const itemName = results?.personalInformation?.full_name;
  const lastActivity = results?.last_activity_date;
  const isAlreadyAccountable = results?.recruiter_id === currentUser.id;

  const restrictions = useProfileRestrictions({
    canRequestDoNotContact,
    canRequestDoNotEngage,
    entityTypeId: ENTITY_TYPE_ID_MAP.candidate,
    profileStatusId: results?.status?.id
  });

  const {
    claimAccountabilityMenuItem,
    handleCloseClaimAccountabilityDrawer,
    handleSuccessClaimAccountability,
    openClaimAccountabilityDrawer
  } = useClaimAccountability({
    entity: CandidateEntity.id,
    isAlreadyAccountable
  });

  const { relation } = results;
  const candidateRelatedWithHiringAuthority = relation?.hiring_authority_id || false;

  const { dataSheet, status: candidateDataSheetStatus } = useFetchCandidateDataSheet(candidateId);

  const { state: candidateTypesState } = useFetchWithStatus(Endpoints.CandidateTypes);

  const getCandidateType = () => {
    if (
      !dataSheet ||
      candidateDataSheetStatus !== RequestStatus.Success ||
      candidateTypesState.status !== UIStatus.Success
    ) {
      return undefined;
    }

    return candidateTypesState.results?.find(
      item => item.id === dataSheet.data.qualification.candidateType.id
    );
  };

  const getCandidateStatus = () => {
    if (!dataSheet) {
      return results?.status;
    }

    return dataSheet.data.qualification.candidateStatus;
  };

  const candidateType = getCandidateType();
  const candidateStatus = getCandidateStatus();

  const candidate = results && {
    ...results,
    status: candidateStatus
  };

  const handleRefreshActivityStatus = data =>
    handleProfileStateUpdate({ last_activity_date: data?.updated_at });
  const formattedDate = localTimeFormatter(lastActivity, DateFormats.SimpleDateTime);

  const triggerTabRefresh = nameTab =>
    setTabKeys(prevState => ({ ...prevState, [nameTab]: new Date().getTime() }));

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   P L A C E M E N T   S E T T I N G S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const referenceReleaseHandler = () => dispatch(hideReferenceReleaseComposerAction());

  const { handleReferenceReleaseComplete } = completeReferenceRelease({
    referenceRelease,
    referenceReleaseHandler
  });

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   S E A R C H   P R O J E C T S   S E T T I N G S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const { SearchProjectForms, SearchProjectsMenuItems, searchProjectTabPropsDefinition } =
    useSearchProjectInProfiles({
      endpoint,
      entity: SearchProjectEntities.Candidate,
      profile: {
        id: candidate?.id,
        name: itemName
      },
      tabKey: tabKeys.searchProjects,
      triggerTabRefresh,
      shouldRedirectToSearchProjectPreview: enableUrlNavigation
    });

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   S E N D O U T S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const {
    handleSendoutClick,
    handleSendoutNavigation,
    handleSendoutCompleted,
    handleSendoutClosed
  } = getSendoutActions({ setUiState, history, triggerTabRefresh });

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *  D A T A   S H E E T
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const handleWriteUpClick = () =>
    history.push(EntityRoutes.CandidateDataSheet.replace(':id', candidateId));

  const tabsProps = createTabsProps({
    candidateId,
    endpoint,
    handleRefreshActivityStatus,
    handleSendoutClick,
    handleWriteUpClick,
    isLoading,
    itemName,
    candidate,
    tabKeys,
    triggerTabRefresh,
    uiState,
    searchProjectsProps: searchProjectTabPropsDefinition,
    overviewProps: {
      candidateDataSheet: dataSheet,
      candidateDataSheetStatus,
      isProfileLoading: isLoading
    }
  });

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   R E F E R E N C E   R E L E A S E
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const { state: referenceState, refreshData: refreshReferenceReleases } = useFetchWithStatus(
    !isLoading && candidate?.id
      ? `${Endpoints.Candidates}/${candidateId}/${Endpoints.ReferenceReleases}`
      : null
  );

  const userHasRightsToEdit = canUserEditEntity(currentUser, candidate, {
    includeCollaborator: true
  });

  const canEdit = userHasRightsToEdit || userHasRoles([Roles.Operations, Roles.DataCoordinator]);

  const referenceReleasePermission = userHasPermission(Permissions.Placements.CanUseModule);
  const sentEmails = referenceState.results;
  const referenceReleaseStatus = referenceState.status;
  const lastEmailSent =
    referenceReleaseStatus === UIStatus.Success && sentEmails?.length && sentEmails[0];
  const sentDate = lastEmailSent && toLocalTime(lastEmailSent?.created_at);
  const formattedSentDate = sentDate && sentDate.format(DateFormats.SimpleDateTime);
  const canViewIfEmailsExist = !userHasRightsToEdit && sentEmails?.length > 0;
  const showReferenceReleaseButton = userHasRightsToEdit || canViewIfEmailsExist;

  const handleReferenceClick = () =>
    dispatch(
      showReferenceReleaseComposer({
        candidateId,
        onReferenceReleaseComplete: async () => {
          await refreshReferenceReleases();
        }
      })
    );

  const headerProps = {
    title: candidate?.personalInformation?.full_name,
    sectionName: 'Candidate'
  };

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   LINK HIRING AUTHORITY TO CANDIDATE
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const handleHiringAuthorityClick = type =>
    setUiState(prevState => ({ ...prevState, [type]: true }));

  const goToHAProfile = idProfile =>
    history.push(EntityRoutes.HiringAuthorityProfile.replace(':id', idProfile));

  const handleAddHiringAuthorityCompleted = async addHiringAuthorityData => {
    setUiState(prevState => ({ ...prevState, isAddHiringAuthorityOpen: false }));
    addHiringAuthorityData && goToHAProfile(addHiringAuthorityData.id);
  };

  const handleLinkHiringAuthorityCompleted = async linkHiringAuthorityData => {
    setUiState(prevState => ({ ...prevState, isLinkHiringAuthorityOpen: false }));
    linkHiringAuthorityData && goToHAProfile(linkHiringAuthorityData.relation.hiring_authority_id);
  };

  const handleHiringAuthorityClosed = type =>
    uiState[type] && setUiState(prevState => ({ ...prevState, [type]: false }));

  const handleUnlinkHiringAuthorityClick = () => {
    const hiringId = relation?.hiring_authority_id;
    const hiringName = relation?.hiringAuthority?.full_name;

    if (hiringId) {
      dispatch(
        confirm({
          severity: 'warning',
          title: strings.inventoryProfiles.sections.candidates.unlinkHiringDialog.title,
          message: strings.formatString(
            strings.inventoryProfiles.sections.candidates.unlinkHiringDialog.message,
            {
              hiringName
            }
          ),
          confirmButtonText: 'Unlink Profiles',
          cancelButtonText: 'Keep Profiles',
          onConfirm: async ok => {
            try {
              if (!ok) return;

              setUiState(prevState => ({ ...prevState, isLoading: true }));
              const response = await API.delete(
                `${Endpoints.Candidates}/${candidateId}/hiring-authorities/${hiringId}/unlink`
              );
              if (response && response.status === 204) {
                dispatch(
                  showAlert({
                    severity: 'success',
                    title: 'Hiring Authority Unlinked Successfully',
                    body: `${hiringName} was unlinked from the Candidate Profile`
                  })
                );

                handleProfileStateUpdate({ relation: null });
              }
            } catch (error) {
              dispatch(
                showAlert({
                  severity: 'error',
                  title: 'Unlink Hiring Authority',
                  autoHideDuration: 5000,
                  body: getErrorMessage(error)
                })
              );
            } finally {
              setUiState(prevState => ({ ...prevState, isLoading: false }));
            }
          }
        })
      );
    }
  };

  const writeUpActions = [
    {
      title: strings.candidates.editDataSheet.contentPageTitle,
      action: handleWriteUpClick,
      visible: canEdit
    },
    {
      title: 'Verify Urgency',
      action: openVerifyUrgencyDialog,
      visible:
        canUseUrgencyVerification &&
        userHasRoles([Roles.RegionalDirector, Roles.AssistantRegionalDirector, Roles.Leadership])
    }
  ];

  const customMenuItems = [
    ...SearchProjectsMenuItems,
    {
      title: 'Reference Release',
      action: handleReferenceClick,
      visible: showReferenceReleaseButton && referenceReleasePermission
    },
    {
      title: strings.inventoryProfiles.common.haAddition,
      action: () => handleHiringAuthorityClick('isAddHiringAuthorityOpen'),
      visible: !candidateRelatedWithHiringAuthority
    },
    {
      title: 'Link with existing Hiring Authority',
      action: () => handleHiringAuthorityClick('isLinkHiringAuthorityOpen'),
      visible: !candidateRelatedWithHiringAuthority
    },
    {
      title: 'Unlink Hiring Authority Profile',
      action: () => handleUnlinkHiringAuthorityClick(),
      visible: candidateRelatedWithHiringAuthority
    },
    {
      title: 'Add Action',
      action: () => setUiState(prev => ({ ...prev, isDailyPlanTaskDrawerOpen: true })),
      visible: isAddDailyPlanTaskEnabled && canUseDailyPlanTask
    },
    {
      title: strings.ai.generators.roleplay.actionButton,
      action: () => handleSelectDrawerType(PROFILE_DRAWER_TYPES.aiRolePlay),
      visible: isRoleplayEnabled
    },
    claimAccountabilityMenuItem,
    ...restrictions.menuItems
  ];

  const handleRefreshProfile = () => refreshData();

  const { optOutDate } = getOptOutDate(candidate);

  const summaryLabels = [
    {
      title: strings.inventoryProfiles.common.referenceRelease,
      icon: lastEmailSent && SvgCheck,
      content: lastEmailSent ? `Sent on ${formattedSentDate}` : 'No email sent yet'
    },
    { title: strings.inventoryProfiles.common.optOut.creation, content: optOutDate }
  ];

  const isUILoading = [candidateDataSheetStatus, status, candidateTypesState.status].includes(
    UIStatus.Loading
  );
  const finalStatus = isUILoading ? UIStatus.Loading : status;

  const handleTaskDrawerClose = () => {
    setUiState(prev => ({ ...prev, isDailyPlanTaskDrawerOpen: false }));
  };

  const handleOnTaskCreated = () => {
    handleTaskDrawerClose();
    history.push(EntityRoutes.DailyPlan);
  };

  return (
    <>
      <InventoryProfileLayout
        activityLabel={formattedDate}
        contentPageLayoutProps={{ className: layoutClassName }}
        additionalHeaderActions={
          <>
            {isPresentationMakerEnabled && (
              <Grid item>
                <FPActionButton
                  icon={SvgMakeAi}
                  onClick={() => handleSelectDrawerType(PROFILE_DRAWER_TYPES.ai)}
                  text={strings.ai.generators.presentationsMaker.actionButton}
                  withGradient
                />
              </Grid>
            )}
            <When condition={!!candidate}>
              <Grid item>
                <ToggleProfileModeButton
                  results={candidate}
                  mode={EntityType.Candidate}
                  {...profileModeProps}
                />
              </Grid>
            </When>
          </>
        }
        entityType={CandidateEntity}
        enableUrlNavigation={enableUrlNavigation}
        headerCardProps={headerProps}
        inventoryType={candidateType}
        results={candidate}
        summaryLabels={summaryLabels}
        profileMenuProps={{
          onReassignClick: handleReassignClick,
          onEditClick: handleEditClick,
          customMenuItems,
          writeUpActions
        }}
        ProfileTabsProps={{
          endpoint,
          organizationStorageKey: PERSISTENCE_KEYS.PROFILE_TABS.CANDIDATES,
          organizationSubheader: `${CandidateEntity.singular} ${strings.inventoryProfiles.title}`,
          profileTabProps: tabsProps
        }}
        ProfileUiStateManagerProps={{
          status: finalStatus,
          responseStatusCode: state.responseStatusCode,
          entityRoute: EntityRoutes.Candidates,
          onRefreshProfileClick: handleRefreshProfile,
          entity: entityType
        }}
      />
      <When condition={!isLoading && candidate?.id}>
        <>
          {candidate.shouldAskRecruiterAddEmployer && (
            <LinkCompanyDialog
              type="candidate"
              entity={candidate}
              onSetProfile={newProfile => handleProfileStateUpdate({ ...newProfile })}
            />
          )}
          {uiState.isSendoutOpen && uiState.selectedSendoutId && (
            <Drawer open onClose={handleSendoutClosed}>
              <SendoutManager
                id={uiState.selectedSendoutId}
                onEdit={handleSendoutCompleted}
                onDelete={handleSendoutCompleted}
                onClose={handleSendoutClosed}
                onNavigate={handleSendoutNavigation}
              />
            </Drawer>
          )}

          <When condition={uiState.isEditOpen}>
            <CandidateEdit
              candidate={candidate}
              onEditCompleted={handleEntityEditionCompleted({
                handleEditCompleted,
                enableUrlNavigation,
                onEditionCompleted
              })}
              onEditClosed={handleEditClosed}
            />
          </When>
          <When condition={uiState.isReassignOpen}>
            <ReassignRecruiterForm
              baseEndpoint={Endpoints.Candidates}
              item={candidate}
              entityType={entityType}
              onReassignCompleted={handleReassignCompleted}
              onReassignClosed={handleReassignClosed}
            />
          </When>

          {referenceRelease?.candidateId && (
            <ReferenceRelease
              candidateId={referenceRelease.candidateId}
              onClose={referenceReleaseHandler}
              onCompleted={handleReferenceReleaseComplete}
            />
          )}
          <SearchProjectForms />

          <When condition={uiState.isAddHiringAuthorityOpen && candidateId}>
            <AddHiringAuthority
              candidate={candidate}
              onClose={() => handleHiringAuthorityClosed('isAddHiringAuthorityOpen')}
              onCompleted={handleAddHiringAuthorityCompleted}
            />
          </When>
          {isOpenVerifyUrgencyDialog && (
            <VerifyUrgencyDialog
              entityId={candidateId}
              entityType={EntityType.Candidate}
              open={isOpenVerifyUrgencyDialog}
              onClose={closeVerifyUrgencyDialog}
              onSaveSucceeded={() => {
                closeVerifyUrgencyDialog();
                onVerificationCompleted && onVerificationCompleted();
              }}
            />
          )}
          {uiState.isLinkHiringAuthorityOpen && candidateId && (
            <LinkHiringAuthorityDrawer
              candidateId={candidateId}
              onClose={() => handleHiringAuthorityClosed('isLinkHiringAuthorityOpen')}
              onCompleted={handleLinkHiringAuthorityCompleted}
            />
          )}
          <When
            condition={isPresentationMakerEnabled && selectedDrawerType === PROFILE_DRAWER_TYPES.ai}
          >
            <PresentationMakerTemplate
              buildSourceParamsFn={buildPresentationMakerCandidateParam}
              generatePresentationFn={generateCandidatePresentation}
              leftContent={({ onSelectPresentationType, isEnabled }) => (
                <CandidatePresentationTypeSelector
                  candidate={candidate}
                  isEnabled={isEnabled}
                  onSelectPresentationType={onSelectPresentationType}
                />
              )}
              onClose={closeDrawer}
              sourceData={candidate}
            />
          </When>

          <When condition={selectedDrawerType === PROFILE_DRAWER_TYPES.aiRolePlay}>
            <PresentationMakerTemplate
              buildSourceParamsFn={buildPresentationMakerCandidateParam}
              generatePresentationFn={generateCandidatePresentation}
              leftContent={({ onSelectPresentationType }) => (
                <CandidateRoleplayScenarioSelector
                  candidate={candidate}
                  onSelectPresentationType={onSelectPresentationType}
                />
              )}
              onClose={closeDrawer}
              rightContentFooter={() => (
                <CallVerbiages callType={CALL_VERBIAGES_TYPES.marketing.id} />
              )}
              sourceData={candidate}
              subtitle={strings.ai.generators.roleplay.subtitle}
              title={strings.ai.generators.roleplay.title}
            />
          </When>
        </>
      </When>
      <When condition={uiState.isDailyPlanTaskDrawerOpen}>
        <CreationTaskDrawer
          onClose={handleTaskDrawerClose}
          entityData={{
            id: results.id,
            name: results?.personalInformation?.full_name
          }}
          entityType={EntityType.Candidate}
          onTaskCreated={handleOnTaskCreated}
        />
      </When>
      <When condition={openClaimAccountabilityDrawer}>
        <ClaimAccountabilityDrawer
          entityId={candidateId}
          entityType={CandidateEntity.id}
          onClose={handleCloseClaimAccountabilityDrawer}
          onSuccess={handleSuccessClaimAccountability}
          open={openClaimAccountabilityDrawer}
        />
      </When>

      <When condition={restrictions.drawerProps.open}>
        <ProfileRestrictionDrawer
          {...restrictions.drawerProps}
          entityId={candidateId}
          entityTypeId={ENTITY_TYPE_ID_MAP.candidate}
        />
      </When>
    </>
  );
};

export default FPCandidateProfile;
